import React, { createContext, useContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { logger } from "@/lib/logger";
import { trackLogin, trackSignup, trackLogout, identifyUser, clearUserIdentity } from "@/lib/analytics";
import { SessionTimeoutWarning } from "@/components/auth/SessionTimeoutWarning";
const getIsNativePlatform = (): boolean => {
  if (typeof window === 'undefined') return false;
  const cap = (window as any).Capacitor;

  // Primary check: Capacitor's own isNativePlatform()
  if (cap?.isNativePlatform?.()) return true;

  // Fallback: when loading from remote URL, isNativePlatform() may return false
  // but getPlatform() still reports 'android' or 'ios'
  const platform = cap?.getPlatform?.();
  if (platform === 'android' || platform === 'ios') return true;

  // Final fallback: check if the native bridge property is set
  if (cap?.isNative === true) return true;

  // Check User Agent for Capacitor signature (often present in WebView)
  const ua = window.navigator.userAgent.toLowerCase();
  if (ua.includes('capacitor')) return true;

  return false;
};

interface DecodedToken {
  sub: string;
  email: string;
  name?: string;
  phone?: string;
  preferred_username?: string;
  realm_access?: {
    roles: string[];
  };
  exp: number;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: DecodedToken | null;
  accessToken: string | null;
  login: (email: string, password: string, redirectTo?: string, otp?: string) => Promise<void>;
  signup: (email: string, password: string, role?: string, redirectTo?: string, firstName?: string, lastName?: string) => Promise<void>;
  logout: () => void;
  hasRole: (role: string) => boolean;
  hasEmployerProfile: boolean;
  employerId: string | null;
  checkEmployerProfile: (token: string) => Promise<boolean>;
  loading: boolean;
  updateUser: (userData: Partial<DecodedToken>) => void;
  refreshUserFromServer: () => Promise<void>;
  googleLogin: () => void;
  handleAuthCallback: (code: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<DecodedToken | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [hasEmployerProfile, setHasEmployerProfile] = useState(() => {
    return localStorage.getItem("hasEmployerProfile") === "true";
  });
  const [employerId, setEmployerId] = useState<string | null>(() => {
    return localStorage.getItem("employerId") || null;
  });
  const [loading, setLoading] = useState(true);

  // Session Timeout State
  const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
  const WARNING_WINDOW_MS = 60 * 1000; // 60 seconds
  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState(60);

  const checkEmployerProfile = React.useCallback(async (token: string) => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "/api";
      const url = `${apiBaseUrl}/employers/me`;
      const profileResponse = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (profileResponse.ok) {
        try {
          const data = await profileResponse.json();
          setHasEmployerProfile(true);
          setEmployerId(data.id);
          localStorage.setItem("hasEmployerProfile", "true");
          localStorage.setItem("employerId", data.id || "");
          logger.auth("Employer profile found and persisted", { employerId: data.id });
          return true;
        } catch (e) {
          setHasEmployerProfile(true);
          localStorage.setItem("hasEmployerProfile", "true");
          return true;
        }
      }

      // 404 is expected when the user hasn't created an employer profile yet
      if (profileResponse.status === 404) {
        logger.debug("No employer profile found (expected for new employers)");
      } else {
        logger.error("Unexpected error checking employer profile", { status: profileResponse.status });
      }
      setHasEmployerProfile(false);
      setEmployerId(null);
      localStorage.removeItem("hasEmployerProfile");
      localStorage.removeItem("employerId");
      return false;
    } catch (profileError) {
      logger.error("Error checking employer profile", profileError);
      setHasEmployerProfile(false);
      setEmployerId(null);
      localStorage.removeItem("hasEmployerProfile");
      localStorage.removeItem("employerId");
      return false;
    }
  }, []);

  // Logout function (defined early for use in refresh token)
  const logout = React.useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8081';
      const realm = import.meta.env.VITE_KEYCLOAK_REALM || 'jobportal';
      const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'job-frontend';

      if (refreshToken) {
        // Call Keycloak logout endpoint to kill the session
        await fetch(`${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            client_id: clientId,
            refresh_token: refreshToken,
          }),
        }).catch(err => console.error("Keycloak logout failed", err));
      }
    } catch (e) {
      console.error("Error during logout", e);
    } finally {
      // Always clear local state
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("hasEmployerProfile");
      localStorage.removeItem("employerId");
      sessionStorage.removeItem("pending_2fa_tokens");
      setAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setHasEmployerProfile(false);
      setEmployerId(null);
      // Track logout event and clear GA4 user identity
      trackLogout();
      clearUserIdentity();
    }
  }, []);

  // Safe parse helper
  const safeParse = async (response: Response) => {
    if (response.status === 204) return null;
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      try {
        return await response.json();
      } catch (e) {
        try {
          return await response.text();
        } catch {
          return null;
        }
      }
    }
    try {
      return await response.text();
    } catch {
      return null;
    }
  };

  // Refresh token function
  const refreshAccessToken = React.useCallback(async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      return false;
    }

    const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8081';
    const realm = import.meta.env.VITE_KEYCLOAK_REALM || 'jobportal';
    const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'job-frontend';

try {
      const tokenUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/token`;
      const response = await fetch(tokenUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          grant_type: "refresh_token",
          client_id: clientId,
          refresh_token: refreshToken,
        }),
      });

      if (!response.ok) {
        // Refresh token is invalid or expired
        logout();
        return false;
      }

      const data = await safeParse(response);

      if (!data || !data.access_token) {
        logout();
        return false;
      }

      const decoded = jwtDecode<DecodedToken>(data.access_token);

      // Default role assignment logic (keep consistent with handleAuthCallback)
      if (!decoded.realm_access) {
        decoded.realm_access = { roles: [] };
      }
      const roles = decoded.realm_access.roles || [];
      const hasDefinedRole = roles.some(r => ["CANDIDATE", "EMPLOYER", "ADMIN"].includes(r));

      if (!hasDefinedRole) {
        if (!decoded.realm_access.roles) decoded.realm_access.roles = [];
        decoded.realm_access.roles.push("CANDIDATE");
      }

      // Store new tokens (rotate refresh token if provided)
      localStorage.setItem("accessToken", data.access_token);
      if (data.refresh_token) {
        localStorage.setItem("refreshToken", data.refresh_token);
      }

      // Also check employer profile if needed
      if (decoded.realm_access?.roles?.includes("EMPLOYER")) {
        await checkEmployerProfile(data.access_token);
      }

      setAccessToken(data.access_token);
      setUser(decoded);
      setIsAuthenticated(true);

      return true;
    } catch (error) {
      logger.error("Failed to refresh token", error);
      logout();
      return false;
    }
  }, [logout]);

  // Check and refresh token if needed
  const checkAndRefreshToken = React.useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      return;
    }

    try {
      const decoded = jwtDecode<DecodedToken>(token);
      if (!decoded.exp) {
        return;
      }

      const expirationTime = decoded.exp * 1000;
      const now = Date.now();
      const timeUntilExpiry = expirationTime - now;

      // Refresh token if it expires in less than 1 minute (60000ms)
      const refreshThreshold = 60 * 1000;

      if (timeUntilExpiry < refreshThreshold && timeUntilExpiry > 0) {
        logger.debug("Access token expiring soon, refreshing...");
        await refreshAccessToken();
      } else if (timeUntilExpiry <= 0) {
        // Token already expired, try to refresh
        logger.debug("Access token expired, attempting refresh...");
        await refreshAccessToken();
      }
    } catch (error) {
      logger.error("Error checking token expiration", error);
    }
  }, [refreshAccessToken]);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        try {
          const decoded = jwtDecode<DecodedToken>(token);

          // Default role assignment logic (keep consistent with handleAuthCallback)
          if (!decoded.realm_access) {
            decoded.realm_access = { roles: [] };
          }
          const roles = decoded.realm_access.roles || [];
          const hasDefinedRole = roles.some(r => ["CANDIDATE", "EMPLOYER", "ADMIN"].includes(r));

          if (!hasDefinedRole) {
            if (!decoded.realm_access.roles) decoded.realm_access.roles = [];
            decoded.realm_access.roles.push("CANDIDATE");
          }
          if (decoded.exp && decoded.exp * 1000 > Date.now()) {
            // Also check employer profile if needed
            if (decoded.realm_access?.roles?.includes("EMPLOYER")) {
              await checkEmployerProfile(token);
            }

            setAccessToken(token);
            setUser(decoded);
            setIsAuthenticated(true);
          } else {
            // Token expired, try to refresh
            await refreshAccessToken();
          }
        } catch (error) {
          logger.error("Failed to decode token", error);
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
        }
      }
      setLoading(false);
    };

    initAuth();

    // Set up interval to check and refresh token every minute
    const tokenCheckInterval = setInterval(() => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        checkAndRefreshToken();
      }
    }, 60 * 1000); // Check every minute

    return () => {
      clearInterval(tokenCheckInterval);
    };
  }, [checkAndRefreshToken, refreshAccessToken, checkEmployerProfile]);

  // Track user activity for session timeout
  useEffect(() => {
    if (!isAuthenticated) return;

    const updateActivity = () => {
      if (!showTimeoutWarning) {
        setLastActivity(Date.now());
      }
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => document.addEventListener(event, updateActivity, { passive: true }));

    return () => {
      events.forEach(event => document.removeEventListener(event, updateActivity));
    };
  }, [isAuthenticated, showTimeoutWarning]);

  // Check idle timeout
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkIdle = setInterval(() => {
      const now = Date.now();
      const idleTime = now - lastActivity;
      
      if (idleTime >= IDLE_TIMEOUT_MS) {
        // Session expired due to inactivity
        clearInterval(checkIdle);
        setShowTimeoutWarning(false);
        logger.auth("Session expired due to inactivity");
        logout();
      } else if (idleTime >= IDLE_TIMEOUT_MS - WARNING_WINDOW_MS) {
        // Show warning
        setShowTimeoutWarning(true);
        const remaining = Math.ceil((IDLE_TIMEOUT_MS - idleTime) / 1000);
        setTimeoutCountdown(remaining > 0 ? remaining : 0);
      } else {
        setShowTimeoutWarning(false);
      }
    }, 1000);

    return () => clearInterval(checkIdle);
  }, [isAuthenticated, lastActivity, logout]);

  const handleStayLoggedIn = async () => {
    try {
      await refreshAccessToken();
      setLastActivity(Date.now());
      setShowTimeoutWarning(false);
    } catch (e) {
      logger.error("Failed to refresh token on stay logged in", e);
    }
  };

  // Listen for programmatic logout events (e.g., apiRequest dispatch when refresh fails)
  useEffect(() => {
    const handleAuthLogout = () => {
      try {
        logout();
      } catch (e) {
        logger.error('Error during auth:logout handler', e);
      }
      try {
        const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
        navigate(`/login?returnTo=${returnTo}`);
      } catch (e) {
        // navigation may fail if router isn't ready; swallow the error
      }
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, [logout, navigate]);

  // Deep link listener for native OAuth callback (Android/iOS)
  // When the system browser completes the Google OAuth flow, Keycloak redirects
  // to https://www.sparkjobs.in/login?code=..., which the OS intercepts as a
  // deep link and passes back to the app via the appUrlOpen event.
  useEffect(() => {
    if (!getIsNativePlatform()) return;

    let cleanup: (() => void) | undefined;

    const setupDeepLinkListener = async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appUrlOpen', async ({ url }) => {
          console.error("[deep link listener] RECEIVED DEEP LINK URL:", url);
          try {
            const urlObj = new URL(url);
            const code = urlObj.searchParams.get('code');
            const state = urlObj.searchParams.get('state');

            if (code) {
              console.error("[deep link listener] Parsed auth code:", code, "state (verifier):", state);
              
              if (state && !localStorage.getItem("pkce_code_verifier") && !sessionStorage.getItem("pkce_code_verifier")) {
                localStorage.setItem("pkce_code_verifier", state);
                console.error("[deep link listener] Re-hydrated pkce_code_verifier in localStorage from state parameter:", state);
              }

              // Close the system browser
              try {
                const { Browser } = await import('@capacitor/browser');
                await Browser.close();
                console.error("[deep link listener] Closed system browser custom tab.");
              } catch (browserErr) {
                console.error("[deep link listener] Error closing browser:", browserErr);
              }

              // Exchange the authorization code for tokens
              const success = await handleAuthCallback(code);
              console.error("[deep link listener] handleAuthCallback result success:", success);
              if (success) {
                // Determine redirect based on user role
                const token = localStorage.getItem("accessToken");
                if (token) {
                  try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    const roles = payload?.realm_access?.roles || [];
                    console.error("[deep link listener] User has roles:", roles);
                    if (roles.includes("ADMIN")) {
                      navigate("/admin-dashboard");
                    } else if (roles.includes("EMPLOYER")) {
                      navigate("/employer-dashboard");
                    } else {
                      navigate("/jobs");
                    }
                  } catch (tokenParseErr) {
                    console.error("[deep link listener] Token payload decode error, navigating to /jobs:", tokenParseErr);
                    navigate("/jobs");
                  }
                } else {
                  console.error("[deep link listener] Success was true but no accessToken found in localStorage! Navigating to /jobs");
                  navigate("/jobs");
                }
              }
            } else {
              console.error("[deep link listener] Deep link URL did not contain 'code' search parameter.");
            }
          } catch (err) {
            console.error("[deep link listener] Deep link OAuth callback failed:", err);
          }
        });

        cleanup = () => handle.remove();
      } catch (err) {
        console.error("[deep link listener] Failed to set up deep link listener:", err);
      }
    };

    setupDeepLinkListener();

    return () => {
      if (cleanup) {
        console.error("[deep link listener] Cleaning up deep link listener...");
        cleanup();
      }
    };
  }, []);

  // PKCE Helper Functions (defined early so login() can use them for 2FA redirect)
  const base64UrlEncode = (array: Uint8Array) => {
    let str = "";
    for (const byte of array) {
      str += String.fromCharCode(byte);
    }
    return btoa(str)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  };

  const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return base64UrlEncode(array);
  };

  const generateCodeChallenge = async (verifier: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await window.crypto.subtle.digest("SHA-256", data);
    return base64UrlEncode(new Uint8Array(digest));
  };

  const login = async (email: string, password: string, redirectTo?: string, otp?: string) => {
    const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8081';
    const realm = import.meta.env.VITE_KEYCLOAK_REALM || 'jobportal';
    const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'job-frontend';
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';

    // Validate environment variables
    if (!keycloakUrl || !realm || !clientId) {
      throw new Error("Keycloak configuration is missing. Please check your environment variables.");
    }

    // Normalize email
    const normalizedEmail = email.trim().toLowerCase();

    // Build the token endpoint URL
    const tokenUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/token`;

    logger.auth("Attempting login", { tokenUrl, clientId, username: normalizedEmail });

    try {
      // ── STEP 1: If OTP was provided, this is the second step of 2FA login ──
      // Verify the OTP through our backend first
      if (otp) {
        logger.auth("Verifying OTP code...");
        const otpResp = await fetch(`${apiBaseUrl}/users/2fa/verify-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: normalizedEmail, code: otp }),
        });

        if (!otpResp.ok) {
          const otpData = await otpResp.json().catch(() => ({}));
          const error = new Error(otpData.error || "Invalid verification code. Please try again.");
          (error as any).status = otpResp.status;
          throw error;
        }

        // OTP verified — retrieve the stored tokens and complete login
        const storedTokens = sessionStorage.getItem("pending_2fa_tokens");
        if (!storedTokens) {
          throw new Error("Session expired. Please log in again.");
        }
        sessionStorage.removeItem("pending_2fa_tokens");

        const data = JSON.parse(storedTokens);
        const decoded = jwtDecode<DecodedToken>(data.access_token);
        logger.auth("OTP verified, completing login", { email: decoded.email });

        localStorage.setItem("accessToken", data.access_token);
        if (data.refresh_token) {
          localStorage.setItem("refreshToken", data.refresh_token);
        }

        setAccessToken(data.access_token);
        setUser(decoded);
        setIsAuthenticated(true);
        // Track 2FA login and identify user in GA4
        trackLogin("2fa");
        identifyUser(decoded.sub, decoded.realm_access?.roles?.find(r => ["CANDIDATE", "EMPLOYER", "ADMIN"].includes(r)));

        // Role-based redirect — always go to dashboard
        // Individual pages handle their own profile checks
        try {
          const roles = decoded?.realm_access?.roles || [];
          if (roles.includes("ADMIN")) {
            navigate(redirectTo || "/admin-dashboard");
          } else if (roles.includes("EMPLOYER")) {
            const hasProfile = await checkEmployerProfile(data.access_token);
            navigate(redirectTo || (hasProfile ? "/employer-dashboard" : "/employer/profile/setup"));
          } else {
            navigate(redirectTo || "/jobs");
          }
        } catch {
          // navigate may fail if router isn't ready
        }
        return;
      }

      // ── STEP 2: Password authentication via Keycloak (no OTP) ──
      const response = await fetch(tokenUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          grant_type: "password",
          client_id: clientId,
          username: normalizedEmail,
          password: password,
          scope: "openid profile email",
        }),
      });

      if (!response.ok) {
        let errorDetail = "Login failed";
        let errorCode = response.status;

        try {
          const errorData = await safeParse(response);
          logger.error("Login failed", errorData);
          if (errorData && typeof errorData === 'object') {
            errorDetail = errorData.error_description || errorData.error || errorDetail;
          } else if (typeof errorData === 'string') {
            errorDetail = errorData;
          }

          // Provide more helpful error messages
          if (errorData && typeof errorData === 'object') {
            const errorDescription = (errorData as any).error_description || '';
            const errorType = (errorData as any).error || '';

            // Check for "account is not fully set up" - redirect to Keycloak interactive login
            if (errorDescription.toLowerCase().includes('account is not fully set up')) {
              const redirectUri = getIsNativePlatform()
                ? 'in.sparkjobs.app://login'
                : window.location.origin + '/login';
              const codeVerifier = generateCodeVerifier();
              const codeChallenge = await generateCodeChallenge(codeVerifier);
              sessionStorage.setItem("pkce_code_verifier", codeVerifier);

              const loginUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid profile email&login_hint=${encodeURIComponent(normalizedEmail)}&code_challenge=${codeChallenge}&code_challenge_method=S256`;

              logger.auth("Redirecting to Keycloak interactive login for required actions");
              window.location.href = loginUrl;
              return;
            } else if (errorDescription.toLowerCase().includes('verify your email') ||
              errorDescription.toLowerCase().includes('email not verified') ||
              errorDescription.toLowerCase().includes('verify_email')) {
              errorDetail = "Email verification required. Please check your email inbox and click the verification link before logging in.";
            } else if (errorType === "invalid_grant") {
              // Check if unverified email via our backend
              try {
                const verifyResp = await fetch(`${apiBaseUrl}/users/check-verification`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: normalizedEmail }),
                });
                if (verifyResp.ok) {
                  const verifyData = await verifyResp.json();
                  if (verifyData.exists && verifyData.requiresVerification) {
                    errorDetail = "Email verification required. Please check your email inbox and click the verification link before logging in.";
                  } else {
                    errorDetail = "Invalid email or password. Please try again.";
                  }
                }
              } catch {
                // Fallback
              }
              errorDetail = errorDetail || "Invalid email or password. Please try again.";
            } else if (errorType === "invalid_client") {
              errorDetail = "Authentication client configuration error. Please contact support.";
            } else if (errorType === "unauthorized_client") {
              errorDetail = "Client not authorized. Please contact support.";
            }
          }
        } catch (e: any) {
          const errorText = await response.text();
          logger.error("Login failed", { status: response.status, error: errorText.substring(0, 100) });
          errorDetail = `Login failed (${response.status}): ${errorText.substring(0, 100)}`;
        }

        const error = new Error(errorDetail);
        (error as any).status = errorCode;
        throw error;
      }

      // ── STEP 3: Password auth succeeded — check if 2FA is required ──
      const data = await safeParse(response);

      if (!data || !data.access_token) {
        logger.error("No access token in response", data);
        throw new Error("Invalid response from auth server: No access token received");
      }

      // Check if user has 2FA enabled in our backend
      let has2FA = false;
      try {
        const checkResp = await fetch(`${apiBaseUrl}/users/2fa/check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: normalizedEmail }),
        });
        if (checkResp.ok) {
          const checkData = await checkResp.json();
          has2FA = checkData.totpConfigured === true;
        }
      } catch {
        // If check fails, assume no 2FA
      }

      if (has2FA) {
        // Store tokens temporarily — don't complete login yet
        sessionStorage.setItem("pending_2fa_tokens", JSON.stringify(data));

        logger.auth("2FA required, prompting for OTP");
        const otpError = new Error("OTP_REQUIRED");
        (otpError as any).status = 401;
        (otpError as any).needsOtp = true;
        (otpError as any).hasTotpConfigured = true;
        throw otpError;
      }

      // ── STEP 4: No 2FA — complete login immediately ──
      const decoded = jwtDecode<DecodedToken>(data.access_token);
      logger.auth("Login successful", { email: decoded.email });

      localStorage.setItem("accessToken", data.access_token);
      if (data.refresh_token) {
        localStorage.setItem("refreshToken", data.refresh_token);
      }

      // We must not call setIsAuthenticated(true) until after the profile check
      // is complete, otherwise Auth.tsx's useEffect will immediately navigate
      // to /employer-dashboard before hasEmployerProfile is resolved, causing 
      // a false-positive redirect to /employer/profile/setup.
      
      let redirectPath = redirectTo || "/jobs";

      // Role-based redirect — always go to dashboard
      // Individual pages handle their own profile checks
      try {
        const roles = decoded?.realm_access?.roles || [];
        logger.auth("Login successful - User roles", { roles });

        if (roles.includes("ADMIN")) {
          redirectPath = redirectTo || "/admin-dashboard";
        } else if (roles.includes("EMPLOYER")) {
          // MUST await profile check before navigating so that
          // hasEmployerProfile & employerId are set in context.
          // Without this, the dashboard sees null employerId and
          // redirects to the setup page again.
          const hasProfile = await checkEmployerProfile(data.access_token);
          if (hasProfile) {
            redirectPath = redirectTo || "/employer-dashboard";
          } else {
            redirectPath = "/employer/profile/setup";
          }
        } else {
          redirectPath = redirectTo || "/jobs";
        }

        // Apply all auth state
        setAccessToken(data.access_token);
        setUser(decoded);
        setIsAuthenticated(true);
        // Track email login and identify user in GA4
        trackLogin("email");
        identifyUser(decoded.sub, decoded.realm_access?.roles?.find(r => ["CANDIDATE", "EMPLOYER", "ADMIN"].includes(r)));
        
        navigate(redirectPath);
      } catch (e) {
        // navigate may fail if router isn't ready; ignore silently
      }
    } catch (error) {
      // Re-throw if it's already an Error with status
      if (error instanceof Error && (error as any).status) {
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(`Cannot connect to authentication server. Please check if Keycloak is running at ${keycloakUrl}`);
      }

      throw error;
    }
  };

  const signup = async (email: string, password: string, role: string = "CANDIDATE", redirectTo?: string, firstName?: string, lastName?: string) => {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';

    // Step 1: Register user in backend/Keycloak
    // Use employer-specific endpoint when role is EMPLOYER (server will force the role)
    const registerPath = role && role.toUpperCase() === "EMPLOYER"
      ? `${apiBaseUrl}/users/register/employer`
      : `${apiBaseUrl}/users/register`;

    const response = await fetch(registerPath, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        password,
        firstName: firstName?.trim() || '',
        lastName: lastName?.trim() || '',
        // role is optional for employer path because server forces EMPLOYER
        ...(role ? { role } : {}),
      }),
    });

    if (!response.ok) {
      let errorMessage = "Signup failed";
      try {
        const errorData = await safeParse(response);
        if (errorData && typeof errorData === 'object') {
          errorMessage = errorData.message || errorData.error || errorMessage;
          logger.error("Signup error", errorData);
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
          logger.error("Signup error", errorData);
        }
      } catch (e) {
        const errorText = await response.text();
        logger.error("Signup error", { status: response.status, error: errorText.substring(0, 100) });
        errorMessage = `Signup failed (${response.status}): ${errorText.substring(0, 100)}`;
      }
      throw new Error(errorMessage);
    }

    const signupData = await safeParse(response);
    logger.auth("User registered successfully", signupData);
    // Track signup event in GA4
    trackSignup("email", role);

    // With email verification enabled, we should not attempt to login automatically
    // The user must verify their email first
    return;
  };

  const updateUser = React.useCallback((userData: Partial<DecodedToken>) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      return { ...prevUser, ...userData };
    });
  }, []);

  const refreshUserFromServer = React.useCallback(async () => {
    if (!accessToken) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/users/me`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const freshUserData = await response.json();
        updateUser({
          name: freshUserData.name,
          phone: freshUserData.phone,
        });
      }
    } catch (error) {
      logger.error("Failed to refresh user data from server", error);
    }
  }, [accessToken, updateUser]);

  const hasRole = (role: string) => {
    return user?.realm_access?.roles?.includes(role) ?? false;
  };

  const googleLogin = async () => {
    const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8081';
    const realm = import.meta.env.VITE_KEYCLOAK_REALM || 'jobportal';
    const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'job-frontend';

    // Use the robust check
    const isNative = getIsNativePlatform();
    const cap = (window as any).Capacitor;

    console.error("[googleLogin] Triggered. isNative:", isNative, "cap.Plugins available:", !!cap?.Plugins, "platform:", cap?.getPlatform?.());

    // IMPORTANT: For Capacitor, the redirectUri must be registered in Keycloak's Valid Redirect URIs.
    // It must match exactly, including the trailing slash if present.
    const redirectUri = isNative
      ? 'in.sparkjobs.app://login'
      : window.location.origin + '/login';

    // Save returnTo from current URL if present
    const currentParams = new URLSearchParams(window.location.search);
    const returnTo = currentParams.get("returnTo");
    if (returnTo) {
      sessionStorage.setItem("auth_return_to", returnTo);
    }

    // Generate PKCE identifier and challenge
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Store verifier in both localStorage and sessionStorage for callback resilience
    localStorage.setItem("pkce_code_verifier", codeVerifier);
    sessionStorage.setItem("pkce_code_verifier", codeVerifier);

    // Pass the verifier via the 'state' parameter to ensure it survives deep-linking.
    const state = codeVerifier;
    const loginUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid profile email offline_access&kc_idp_hint=google&code_challenge=${codeChallenge}&code_challenge_method=S256&state=${state}`;

    console.error("[googleLogin] Final loginUrl generated. Navigating...");

    if (isNative) {
      // On Android/iOS, open in system browser (Chrome Custom Tab)
      try {
        // Try global Capacitor.Plugins first for remote-loaded sites
        const Browser = cap?.Plugins?.Browser;
        if (Browser) {
          console.error("[googleLogin] Using Capacitor.Plugins.Browser.open");
          await Browser.open({ url: loginUrl });
        } else {
          console.error("[googleLogin] Capacitor.Plugins.Browser not found, trying dynamic import...");
          const { Browser: BrowserMod } = await import('@capacitor/browser');
          await BrowserMod.open({ url: loginUrl });
        }
        console.error("[googleLogin] Browser open successful.");
      } catch (err) {
        console.error("[googleLogin] Failed to open system browser, falling back to window.location.href:", err);
        window.location.href = loginUrl;
      }
    } else {
      console.error("[googleLogin] Web platform detected, using window.location.href");
      window.location.href = loginUrl;
    }
  };

  async function handleAuthCallback(code: string) {
    const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8081';
    const realm = import.meta.env.VITE_KEYCLOAK_REALM || 'jobportal';
    const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'job-frontend';

    // Ensure redirectUri matches what was used in googleLogin
    const isNativeForRedirect = getIsNativePlatform();
    const redirectUri = isNativeForRedirect
      ? 'in.sparkjobs.app://login'
      : window.location.origin + '/login';

    // Retrieve PKCE verifier from localStorage or sessionStorage
    const codeVerifier = localStorage.getItem("pkce_code_verifier") || sessionStorage.getItem("pkce_code_verifier");

    console.error("[handleAuthCallback] Token exchange starting. params:", { 
      clientId,
      redirectUri, 
      hasCodeVerifier: !!codeVerifier
    });
    const tokenUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/token`;

    try {
      const bodyParams = new URLSearchParams({
        grant_type: "authorization_code",
        client_id: clientId,
        code: code,
        redirect_uri: redirectUri,
      });

      // Add code_verifier if it exists (it should for PKCE flow)
      if (codeVerifier) {
        bodyParams.append("code_verifier", codeVerifier);
      } else {
        console.error("[handleAuthCallback] WARNING: NO PKCE CODE VERIFIER FOUND IN STORAGE!");
      }

      console.error("[handleAuthCallback] Dispatching fetch to tokenUrl:", tokenUrl);

      const response = await fetch(tokenUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: bodyParams,
      });

      // Clean up verifier
      localStorage.removeItem("pkce_code_verifier");
      sessionStorage.removeItem("pkce_code_verifier");
      console.error("[handleAuthCallback] Removed pkce_code_verifier from storage.");

      if (!response.ok) {
        let errorMsg = `HTTP ${response.status}`;
        try {
          const errJson = await response.json();
          errorMsg = errJson.error_description || errJson.error || errorMsg;
        } catch {
          const errorText = await response.text().catch(() => "");
          if (errorText) errorMsg = errorText;
        }
        console.error("[handleAuthCallback] Token exchange error:", response.status, errorMsg);
        throw new Error(errorMsg);
      }

      const data = await safeParse(response);

      if (!data || !data.access_token) {
        throw new Error("No access token received");
      }

      const decoded = jwtDecode<DecodedToken>(data.access_token);

      // Default role assignment if missing (e.g. Google login without mapper)
      if (!decoded.realm_access) {
        decoded.realm_access = { roles: [] };
      }

      // If user has no specific roles, assign CANDIDATE by default
      // This ensures Google login users get the candidate experience even if Keycloak mapper is missing
      const roles = decoded.realm_access.roles || [];
      const hasDefinedRole = roles.some(r => ["CANDIDATE", "EMPLOYER", "ADMIN"].includes(r));

      if (!hasDefinedRole) {
        if (!decoded.realm_access.roles) decoded.realm_access.roles = [];
        decoded.realm_access.roles.push("CANDIDATE");
        logger.auth("Assigned default CANDIDATE role to user");
      }

      localStorage.setItem("accessToken", data.access_token);
      if (data.refresh_token) {
        localStorage.setItem("refreshToken", data.refresh_token);
      }

      if (decoded.realm_access?.roles?.includes("EMPLOYER")) {
        await checkEmployerProfile(data.access_token);
      }

      setAccessToken(data.access_token);
      setUser(decoded);
      setIsAuthenticated(true);
      // Track Google login and identify user in GA4
      trackLogin("google");
      identifyUser(decoded.sub, decoded.realm_access?.roles?.find(r => ["CANDIDATE", "EMPLOYER", "ADMIN"].includes(r)));

      return true;
    } catch (error) {
      logger.error("Auth callback failed", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        accessToken,
        login,
        signup,
        logout,
        hasRole,
        hasEmployerProfile,
        employerId,
        checkEmployerProfile,
        updateUser,
        refreshUserFromServer,
        loading,
        googleLogin,
        handleAuthCallback,
      }}
    >
      {children}
      <SessionTimeoutWarning
        isOpen={showTimeoutWarning}
        remainingSeconds={timeoutCountdown}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={() => {
          setShowTimeoutWarning(false);
          logout();
        }}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

